import FileUpload from "@nkr/components/file-upload";
import { useState } from "react";
import { Alert, Button, MenuItem, Modal } from "react-bootstrap";
import { DropEvent, FileRejection } from "react-dropzone";
import * as Papa from 'papaparse';
import { ITransactionConfirmation } from "@nkr/models/reports/accounts-payable";



type TProps = {
    onConfirmationFileUploaded: (transactionConfirmations: ITransactionConfirmation[]) => ITransactionConfirmation[]
}

export default function ConfirmationFileModal({ onConfirmationFileUploaded }: TProps) {
    const [show, setShow] = useState<boolean>(false);
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>('');
    const [fallout, setFallout] = useState<ITransactionConfirmation[]>();

    const cancel = () => {
        setFiles([]);
        setError('');
        setFallout([]);
        setShow(false);
    }

    const handleFileDropped = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        setFiles(acceptedFiles);
        setError('');
        setFallout([]);
    };

    const handleRemoveFile = () => {
        setFiles([]);
        setError('');
        setFallout([]);
    };

    const fileValid = (data: any[]): boolean => {
        if (!data || data.length === 0) return false;
        return data.every(row =>
            'payTo' in row
            && 'chaseTransactionNumber' in row
            && 'amount' in row
        );
    }

    const handleSubmit = () => {
        if (files[0]) {
            Papa.parse(files[0], {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: true,
                transformHeader: (header) => {
                    return header.split(' ').map((word, i) => i === 0
                        ? word.charAt(0).toLowerCase() + word.slice(1)
                        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join('');
                },
                complete: (results) => {
                    if (fileValid(results.data)) {
                        const fell = onConfirmationFileUploaded(results.data as ITransactionConfirmation[]);
                        if (fell.length > 0) {
                            setFallout(fell)
                        } else {
                            cancel();
                        }

                    } else {
                        setFiles([]);
                        setFallout([]);
                        setError('Invalid file format - please fix and resubmit.');
                    }
                },
                error: () => {
                    setFiles([]);
                    setFallout([]);
                    setError('Invalid file format - please fix and resubmit.');
                }
            });
        }
    };

    return (<>
        <MenuItem onClick={() => setShow(true)}>Import Chase ACH Trxns</MenuItem>
        <Modal backdrop='static' show={show} onHide={cancel} bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title>
                    Import Chase ACH Transaction Confirmation
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {fallout && fallout.length > 0
                    ? <>
                        <Alert bsStyle="warning">
                            The following {fallout.length} record(s) could not be matched to a pending payment.
                        </Alert>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-striped table-condensed table-bordered" style={{ whiteSpace: 'nowrap' }}>
                                <thead>
                                    <tr>
                                        <th>Pay To</th>
                                        <th>Pay From</th>
                                        <th>Chase Trxn #</th>
                                        <th>Status</th>
                                        <th>Send On</th>
                                        <th>Deliver By</th>
                                        <th style={{ textAlign: 'right' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fallout.map((row, i) => (
                                        <tr key={i}>
                                            <td>{row.payTo}</td>
                                            <td>{row.payFrom}</td>
                                            <td>{row.chaseTransactionNumber}</td>
                                            <td>{row.status}</td>
                                            <td>{row.sendOn}</td>
                                            <td>{row.deliverBy}</td>
                                            <td style={{ textAlign: 'right' }}>{row.amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                    : <>
                        <FileUpload
                            label="Click to Select or Drag and Drop the Chase ACH Transaction Confirmation file ..."
                            files={files}
                            accept={['.csv']}
                            singleUploadOnly={true}
                            onFileDropped={handleFileDropped}
                            onRemoveFile={handleRemoveFile}
                        />
                    </>
                }
                {error && <Alert bsStyle="danger" style={{ marginTop: '20px' }}>{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                {fallout && fallout.length > 0
                    ? <Button bsStyle="primary" onClick={cancel} >Ok</Button>
                    : <>
                        <Button disabled={!(files && files.length > 0)} bsStyle='primary' onClick={handleSubmit}>Process</Button>
                        <Button onClick={cancel} >Cancel</Button>
                    </>}
            </Modal.Footer>
        </Modal>
    </>);
}
